<script setup>
  import { ref, computed, onMounted, onErrorCaptured } from 'vue';
  import { useProForecastStore } from '@/stores/ProForecastStore';
  import { useUserStore } from '@/stores/UserStore';
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import ProForecast from '@/components/ProForecast.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';
  import ToolbarHeaderActionListBase from '@/components/ToolbarHeaderActionListBase.vue';
  import ToolbarHeaderActionMenuPopup from '@/components/ToolbarHeaderActionMenuPopup.vue';
  import ModalSaveContainerToProfile from '@/components/ModalSaveContainerToProfile.vue';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ModalBrandPromo from '@/components/ModalBrandPromo.vue';
  import Icon from '@/components/icons/Icon.vue';
  import useContainerConfigurationGlobals from '@/composables/useContainerConfigurationGlobals';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import { findMatchingProfileContainer } from '@/helpers/findMatchingProfileContainer';

  const { $PRO_FORECAST } = useContainerConfigurationGlobals();
  const { $PROMO_MODAL_ALLOWED } = useSettingsGlobals(); 
  const { pro_forecast_id, is_sample_request} = $PRO_FORECAST;  

  const proForecastStore = useProForecastStore();
  const userStore = useUserStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  const showSample = is_sample_request || userStore.memberLevel < 49;

  const loadFooterAd = ref(false);
  const showFavoriteModal = ref(false);
  const error = ref(null);

  const showPromoModal = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49 && $PROMO_MODAL_ALLOWED);
  const showFavoriteAction = computed(() => userStore.memberLevel >= 49 && !showSample);
  const favoriteName = computed(() => `${proForecastStore.proForecastName} - Pro Forecast`);
  const favoriteSettings = computed(() => ({ forecastId: pro_forecast_id }));
  const isProForecastFavorited = computed(() => userStore.userProfiles?.profiles?.some(profile => (
    Boolean(findMatchingProfileContainer(profile, 'Operational Forecast', favoriteSettings.value))
  )) || false);

  function handleShowFavoriteModal() {
    showFavoriteModal.value = true;
  }

  onMounted(() => {
    loadFooterAd.value = true;
  });

  onErrorCaptured((e) => {
    console.warn(e);

    error.value = { 
      title: `There was a problem loading the Pro Forecast (${pro_forecast_id})`,
      message: `Please try again later.`,
      errorText: e.message
    }

    console.warn('There was an error retrieving the Pro Forecast.', e);

    return false;
  });

  await proForecastStore.getProForecast(pro_forecast_id, showSample);
  await proForecastStore.getForecasters();

  if (showFavoriteAction.value && !userStore.userProfiles) {
    await userStore.getProfiles();
  }
</script>

<template>
  <ErrorBlock v-if="error" :error="error" />
  <ProForecast ref="pro-forecast-container" :isSample="showSample" v-else>
    <template #toolbarActions>
      <ToolbarHeaderActionListBase v-if="showFavoriteAction && isDesktop">
        <template #default="{ classes }">
          <li :class="classes.item">
            <button
              type="button"
              :class="classes.button"
              @click="handleShowFavoriteModal"
            >
              <Icon v-if="isProForecastFavorited" name="star-solid" :class="classes.icon" data-selected />
              <Icon v-else name="star" :class="classes.icon" />
              <span :class="classes.label">Favorite</span>
            </button>
          </li>
        </template>
      </ToolbarHeaderActionListBase>

      <ToolbarHeaderActionMenuPopup v-else-if="showFavoriteAction">
        <template #menu-content>
          <ToolbarHeaderActionListBase>
            <template #default="{ classes }">
              <li :class="classes.item">
                <button
                  type="button"
                  :class="classes.button"
                  @click="handleShowFavoriteModal"
                >
                  <Icon v-if="isProForecastFavorited" name="star-solid" :class="classes.icon" data-selected />
                  <Icon v-else name="star" :class="classes.icon" />
                  <span :class="classes.label">Favorite</span>
                </button>
              </li>
            </template>
          </ToolbarHeaderActionListBase>
        </template>
      </ToolbarHeaderActionMenuPopup>
    </template>
  </ProForecast>
  <ModalSaveContainerToProfile
    v-if="showFavoriteModal"
    v-model:showModal="showFavoriteModal"
    containerTypeId="Operational Forecast"
    containerTypeName="Pro Forecast"
    :defaultName="favoriteName"
    :contextName="proForecastStore.proForecastName"
    :settings="favoriteSettings"
  />
  <Suspense>
    <ModalSiteSettings />
  </Suspense>  
  <ModalBrandPromo v-if="showPromoModal" />
</template>
